import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Minus, Loader2, ArrowUpDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatDate } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { useSelectableSensors } from '../../hooks';
import type { QuoteSensor, SensorSelectParams } from '../../types';

// Shared with InvoiceSensorsTable (invoice editor) so the linked-sensors list
// and this picker — both visible in the same invoice modal — render identical
// status badges.
export const SENSOR_STATUS_LABELS: Record<number, { label: string; variant: 'secondary' | 'success' | 'warning' | 'destructive' | 'outline' }> = {
  0: { label: 'Unmanaged', variant: 'secondary' },
  1: { label: 'Active', variant: 'success' },
  2: { label: 'Too Low', variant: 'warning' },
  3: { label: 'Too High', variant: 'warning' },
  4: { label: 'To Acknowledge', variant: 'warning' },
  5: { label: 'Inactive', variant: 'destructive' },
  6: { label: 'Maintenance', variant: 'outline' },
  8: { label: 'Inhibited', variant: 'secondary' },
  9: { label: 'Archived', variant: 'secondary' },
};

const STATUS_FILTER_OPTIONS = [
  { value: 0, label: 'Unmanaged' },
  { value: 1, label: 'Active' },
  { value: 2, label: 'Too Low' },
  { value: 3, label: 'Too High' },
  { value: 4, label: 'To Acknowledge' },
  { value: 5, label: 'Inactive' },
  { value: 6, label: 'Maintenance' },
  { value: 8, label: 'Inhibited' },
  { value: 9, label: 'Archived' },
];

interface SensorSelectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  /**
   * Reserved for the future PG-native (V4) cutover. Currently unused — the
   * picker reads sensors live from Mongo (V3) via useSelectableSensors.
   */
  pgAccountId?: string;
  quoteSensorIds: Set<string>;
  onAddSensor: (sensor: QuoteSensor) => void;
  onRemoveSensor: (sensorId: string) => void;
}

export function SensorSelectDialog({
  open,
  onOpenChange,
  customerId,
  quoteSensorIds,
  onAddSensor,
  onRemoveSensor,
}: SensorSelectDialogProps) {
  const { t } = useTranslation('quotes');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState('name');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);
  // Client-side sort for Calibration / Installation columns. The V4 account-based
  // sensor endpoint doesn't return these fields, and they live behind populated refs
  // on the V3 path, so sorting them server-side isn't reliable — we sort the loaded
  // page here instead.
  const [clientSort, setClientSort] = useState<{ key: 'calibration' | 'installation'; direction: 1 | -1 } | null>(null);
  const [statusFilters, setStatusFilters] = useState<number[]>([]);
  const [nameFilter, setNameFilter] = useState('');
  const [streamFilter, setStreamFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [calDateFrom, setCalDateFrom] = useState('');
  const [calDateTo, setCalDateTo] = useState('');
  const [pageLength, setPageLength] = useState(25);

  // Reset filters/search/page each time the picker opens, so a stale filter from a
  // prior session doesn't leave the list looking empty and the user feeling stuck.
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setPage(0);
    setSortKey('name');
    setSortDirection(1);
    setStatusFilters([]);
    setNameFilter('');
    setStreamFilter('');
    setLocationFilter('');
    setCalDateFrom('');
    setCalDateTo('');
    setClientSort(null);
  }, [open]);

  const debouncedSearch = useDebounce(search, 300);
  const debouncedNameFilter = useDebounce(nameFilter, 300);
  const debouncedStreamFilter = useDebounce(streamFilter, 300);
  const debouncedLocationFilter = useDebounce(locationFilter, 300);

  const params: SensorSelectParams = {
    pageNumber: page,
    pageLength,
    sortKey,
    sortDirection,
    search: debouncedSearch || undefined,
    nameSearch: debouncedNameFilter || undefined,
    streamSearch: debouncedStreamFilter || undefined,
    locationSearch: debouncedLocationFilter || undefined,
    status_in: statusFilters.length > 0 ? statusFilters : undefined,
  };

  // Read the customer's sensors live from MongoDB (V3) — the same source the
  // legacy V1 app uses. The V4/PostgreSQL path reads the accounts mirror, which
  // froze 2026-05-21 when accounts_sync CDC was decommissioned: it serves stale
  // stream/sensor identity (wrong sensor for a stream) and drops calibration
  // data. useSelectableSensors is the single read seam — when sensors become
  // PG-native, repoint it (and getSelectableSensors) at the V4 account endpoint.
  const { data, isLoading } = useSelectableSensors(
    open ? customerId : undefined,
    params
  );
  const rawSensors = useMemo(() => data?.data || [], [data?.data]);
  const total = data?.total || 0;

  // Client-side calibration date filter + Calibration/Installation sort (within current page)
  const sensors = useMemo(() => {
    let list = rawSensors;
    if (calDateFrom || calDateTo) {
      list = list.filter((s) => {
        const calDate = s.stream?.calibration?.date;
        if (!calDate) return false;
        const d = calDate.slice(0, 10);
        if (calDateFrom && d < calDateFrom) return false;
        if (calDateTo && d > calDateTo) return false;
        return true;
      });
    }
    if (clientSort) {
      const getVal = (s: QuoteSensor) =>
        clientSort.key === 'calibration'
          ? s.stream?.calibration?.date || ''
          : s.installationqualification?.date || '';
      list = [...list].sort((a, b) => {
        const av = getVal(a);
        const bv = getVal(b);
        // Empty values always sort to the bottom regardless of direction.
        if (!av && !bv) return 0;
        if (!av) return 1;
        if (!bv) return -1;
        return av < bv ? -clientSort.direction : av > bv ? clientSort.direction : 0;
      });
    }
    return list;
  }, [rawSensors, calDateFrom, calDateTo, clientSort]);

  const totalPages = Math.ceil(total / pageLength);

  const toggleStatus = (status: number) => {
    setStatusFilters((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
    setPage(0);
  };

  const handleSort = (key: string) => {
    setClientSort(null); // server-side sort takes over
    if (sortKey === key) {
      setSortDirection((d) => (d === 1 ? -1 : 1));
    } else {
      setSortKey(key);
      setSortDirection(1);
    }
    setPage(0);
  };

  const handleClientSort = (key: 'calibration' | 'installation') => {
    setClientSort((prev) =>
      prev && prev.key === key
        ? { key, direction: prev.direction === 1 ? -1 : 1 }
        : { key, direction: 1 },
    );
  };

  const selectableOnPage = useMemo(
    () => sensors.filter((s) => !quoteSensorIds.has(s._id)),
    [sensors, quoteSensorIds]
  );

  const handleSelectAll = () => {
    selectableOnPage.forEach((s) => onAddSensor(s));
  };

  const statusFilterLabel =
    statusFilters.length > 0 ? `Status (${statusFilters.length})` : 'Any';

  const SortHead = ({ label, field }: { label: string; field: string }) => (
    <th
      className="text-left px-2 py-2 font-medium text-xs cursor-pointer select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortKey === field && <ArrowUpDown className="h-3 w-3" />}
      </div>
    </th>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t('sensors.selectSensors')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 flex-1 min-h-0 overflow-y-auto pr-1">
          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              placeholder={t('sensors.filter')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="h-8 text-sm max-w-[200px]"
            />
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground whitespace-nowrap">Cal. date:</span>
              <Input
                type="date"
                className="h-8 text-xs w-[130px]"
                value={calDateFrom}
                onChange={(e) => { setCalDateFrom(e.target.value); setPage(0); }}
                title="Calibration from"
              />
              <span className="text-xs text-muted-foreground">–</span>
              <Input
                type="date"
                className="h-8 text-xs w-[130px]"
                value={calDateTo}
                onChange={(e) => { setCalDateTo(e.target.value); setPage(0); }}
                title="Calibration to"
              />
            </div>
            {selectableOnPage.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                {t('sensors.selectAll')} ({selectableOnPage.length})
              </Button>
            )}
          </div>

          {/* Table */}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <SortHead label={t('sensors.customId')} field="customid" />
                  <SortHead label={t('sensors.name')} field="name" />
                  <SortHead label={t('sensors.location')} field="location.name" />
                  <SortHead label={t('sensors.stream')} field="stream.name" />
                  <th className="text-left px-2 py-2 font-medium text-xs">{t('sensors.userGroup')}</th>
                  <th className="text-left px-2 py-2 font-medium text-xs">{t('sensors.sensorGroups')}</th>
                  <th
                    className="text-left px-2 py-2 font-medium text-xs cursor-pointer select-none"
                    onClick={() => handleClientSort('calibration')}
                  >
                    <div className="flex items-center gap-1">
                      {t('sensors.calibration')}
                      {clientSort?.key === 'calibration' && <ArrowUpDown className="h-3 w-3" />}
                    </div>
                  </th>
                  <th
                    className="text-left px-2 py-2 font-medium text-xs cursor-pointer select-none"
                    onClick={() => handleClientSort('installation')}
                  >
                    <div className="flex items-center gap-1">
                      {t('sensors.installation')}
                      {clientSort?.key === 'installation' && <ArrowUpDown className="h-3 w-3" />}
                    </div>
                  </th>
                  <SortHead label={t('sensors.status')} field="status" />
                  <th className="px-2 py-2 w-10"></th>
                </tr>
                <tr className="border-t bg-background">
                  <th className="px-2 py-1"></th>
                  <th className="px-2 py-1">
                    <Input
                      placeholder="Filter name..."
                      value={nameFilter}
                      onChange={(e) => {
                        setNameFilter(e.target.value);
                        setPage(0);
                      }}
                      className="h-7 text-xs"
                    />
                  </th>
                  <th className="px-2 py-1">
                    <Input
                      placeholder="Filter location..."
                      value={locationFilter}
                      onChange={(e) => {
                        setLocationFilter(e.target.value);
                        setPage(0);
                      }}
                      className="h-7 text-xs"
                    />
                  </th>
                  <th className="px-2 py-1">
                    <Input
                      placeholder="Filter stream..."
                      value={streamFilter}
                      onChange={(e) => {
                        setStreamFilter(e.target.value);
                        setPage(0);
                      }}
                      className="h-7 text-xs"
                    />
                  </th>
                  <th className="px-2 py-1"></th>
                  <th className="px-2 py-1"></th>
                  <th className="px-2 py-1"></th>
                  <th className="px-2 py-1"></th>
                  <th className="px-2 py-1">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 w-full justify-start text-xs font-normal"
                        >
                          {statusFilterLabel}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-44">
                        {STATUS_FILTER_OPTIONS.map((opt) => (
                          <DropdownMenuCheckboxItem
                            key={opt.value}
                            checked={statusFilters.includes(opt.value)}
                            onCheckedChange={() => toggleStatus(opt.value)}
                          >
                            {opt.label}
                          </DropdownMenuCheckboxItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </th>
                  <th className="px-2 py-1"></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={10} className="text-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : sensors.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-8 text-muted-foreground">
                      {t('sensors.noResults')}
                    </td>
                  </tr>
                ) : (
                  sensors.map((sensor) => {
                    const isOnQuote = quoteSensorIds.has(sensor._id);
                    const cal = sensor.stream?.calibration;
                    const iq = sensor.installationqualification;

                    return (
                      <tr key={sensor._id} className="border-t">
                        <td className="px-2 py-1.5 text-xs">{sensor.customid || '\u2014'}</td>
                        <td className="px-2 py-1.5 text-xs font-medium">{sensor.name}</td>
                        <td className="px-2 py-1.5 text-xs">{sensor.location?.name || '\u2014'}</td>
                        <td className="px-2 py-1.5 text-xs">{sensor.stream?.name || '\u2014'}</td>
                        <td className="px-2 py-1.5 text-xs">{sensor.usergroup || '\u2014'}</td>
                        <td className="px-2 py-1.5 text-xs">{sensor.sensorgroups?.map((g) => g.name).join(', ') || '\u2014'}</td>
                        <td className="px-2 py-1.5 text-xs">
                          {cal?.cert_number ? (
                            <span>
                              {cal.cert_number}
                              {cal.date && <span className="text-muted-foreground ml-1">({formatDate(cal.date)})</span>}
                            </span>
                          ) : '\u2014'}
                        </td>
                        <td className="px-2 py-1.5 text-xs">
                          {iq?.report_number ? (
                            <span>
                              {iq.report_number}
                              {iq.date && <span className="text-muted-foreground ml-1">({formatDate(iq.date)})</span>}
                            </span>
                          ) : '\u2014'}
                        </td>
                        <td className="px-2 py-1.5 text-xs">
                          {sensor.status != null ? (() => {
                            const statusInfo = SENSOR_STATUS_LABELS[sensor.status];
                            return (
                              <Badge variant={statusInfo?.variant ?? 'outline'} className="text-[10px]">
                                {statusInfo?.label ?? sensor.status}
                              </Badge>
                            );
                          })() : '—'}
                        </td>
                        <td className="px-2 py-1.5">
                          {isOnQuote ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-7 w-7 border-destructive/60 text-destructive hover:bg-destructive hover:text-white"
                              onClick={() => onRemoveSensor(sensor._id)}
                              title={t('sensors.removeFromQuote')}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-7 w-7 border-green-600/60 text-green-600 hover:bg-green-600 hover:text-white"
                              onClick={() => onAddSensor(sensor)}
                              title={t('sensors.addToQuote')}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > 0 && (
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">
                  {total} {t('sensors.sensorsTotal')}
                </span>
                <select
                  className="h-7 rounded-md border border-input bg-background px-2 text-xs"
                  value={pageLength}
                  onChange={(e) => {
                    setPageLength(Number(e.target.value));
                    setPage(0);
                  }}
                  aria-label={t('table.perPage')}
                >
                  {[25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size} / {t('table.perPage')}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  {t('table.previous')}
                </Button>
                <span className="flex items-center px-2 text-muted-foreground">
                  {page + 1} / {Math.max(totalPages, 1)}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  {t('table.next')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
